import React from 'react';
import { motion } from 'framer-motion';
import { Fish, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const AquacultureIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Passive Fish Screens',
      description: 'Low-velocity, fish-safe intake screens meeting NOAA/WDFW criteria',
      link: '/filtration/suction-screens/passive-fish-screens'
    },
    {
      title: 'Inline Pressure Filters',
      description: 'Recirculating aquaculture systems (RAS) polishing',
      link: '/filtration/inline/big'
    },
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Custom configurations for hatcheries and grow-out facilities',
      link: '/filtration/suction-screens/self-cleaning'
    }
  ];

  const benefits = [
    'Low-velocity, fish-safe intake screens meeting NOAA/WDFW open-area criteria',
    'Inline pressure filters for recirculating aquaculture systems (RAS)',
    'Protect pumps, heat exchangers, and oxygenation equipment',
    'Custom configurations for hatcheries, grow-out facilities, and flow-through systems',
    'Stainless-steel construction for freshwater and saltwater environments',
    'Maintain clean, oxygen-rich water quality critical to fish health'
  ];

  const applications = [
    {
      title: 'Hatchery Intake Protection',
      description: 'Fish-safe screens for surface-water intakes protecting juvenile fish while maintaining consistent water flow for hatchery operations.'
    },
    {
      title: 'Tank Recirculation',
      description: 'Inline filtration for grow-out tanks and raceway systems, removing solids and maintaining water quality for optimal fish health.'
    },
    {
      title: 'RAS Polishing',
      description: 'Fine filtration for recirculating aquaculture systems, protecting biofilters and UV units while maintaining system efficiency.'
    },
    {
      title: 'Pond and Canal Filtration',
      description: 'Self-cleaning suction screens for pond and canal-based systems, handling variable debris loads and seasonal water-quality changes.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Aquaculture & Fisheries Filtration Systems | CTC Waterworks"
        description="Fish-safe filtration for hatcheries, RAS, and surface-water intakes. NOAA/WDFW compliant options available."
        canonicalPath="/industries/aquaculture"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Fish className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Aquaculture & Fisheries
                  </h1>
                </div>
                <p className="text-2xl text-blue-100 mb-6 font-light leading-relaxed">
                  Consistent water quality is critical to fish health and hatchery performance. CTC filtration systems maintain clean, oxygen-rich environments and protect pumps, exchangers, and oxygenation equipment.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-blue-100">Low-velocity, fish-safe intake screens meeting NOAA/WDFW open-area criteria</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-blue-100">Inline pressure filters for recirculating aquaculture systems (RAS)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={24} />
                    <span className="text-blue-100">Custom configurations for hatcheries, grow-out facilities, and flow-through systems</span>
                  </li>
                </ul>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=aquaculture-filtration"
                  className="block w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Get design assistance, submittals, and spec packs for your next project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Aquaculture & Fisheries Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-blue-50 rounded-lg p-6 border border-blue-200 hover:border-blue-600 hover:shadow-lg transition-all"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Recommended Filtration Systems
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {systems.map((system, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={system.link}
                    className="block bg-white rounded-lg shadow-lg p-6 h-full hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-600 group"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600">
                      {system.title}
                    </h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">{system.description}</p>
                    <span className="inline-flex items-center text-blue-600 font-semibold">
                      Learn More <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why CTC for Aquaculture Operations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <CheckCircle className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Engineer Your Solution</h2>
            <p className="text-xl text-blue-100 mb-8">
              From hatchery intake protection to RAS polishing, our systems are built to maintain optimal water quality for fish health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=aquaculture-filtration"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
              >
                <span>Talk to a Filtration Expert</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="tel:+15099017476"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                <span>(509) 901-7476</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AquacultureIndustryPage;
